/**
 * 동적 데이터셋(dataSources, DataSourceDef) — 작성폼 행 그리드(RowsCollectionEditor) 렌더용
 * 순수 어댑터/선별 헬퍼 + 행 그리드 상태 변환 로직(구 rowsCollection.ts 흡수, D4-1).
 *
 * D1에서 shared DataSourceFieldMap.widget/widgetKey/editable 어휘가 행 위젯 어휘로 통합되었으므로,
 * 선언 필드 명세를 얇게 매핑해 행 그리드 계약(DataSourceRowField)으로 넘긴다
 * (feedback-composer-no-domain-specific-widget-in-core).
 *
 * 행 객체는 flat 구조(`{ rowId, <field>: value, ... }`)이며 field는 행 객체 내 flat 키다
 * (양식 반복행 셀 바인딩의 field 경로와 동일 정본 — 서버 seed 조립본과 동일 shape).
 * - 편집본: content[def.key] = RowObject[]
 * - AI/서버 조립 원본(seed): content[def.key + ROWS_SEED_SUFFIX] = RowObject[]  (되돌리기 대조용)
 */
import type {
  DataSourceDef,
  DataSourceFieldMap,
  DataSourceFieldWidget,
} from '@safetyhub/shared';
import { evaluateCondition } from '@safetyhub/shared';
import { setByPath } from './resolver';
// 날짜/기간 표시 규약(순수) — 요약 헤더가 그리드 본문과 같은 글자로 접기 위해 공유한다.
import { dateWidgetDisplayText } from './dateValue';

/**
 * 행 필드(칼럼) 위젯 명세 — RowsCollectionEditor가 소비하는 로컬 계약.
 * (구 shared WriteFormRowField — 작성폼 rows 경로 제거(D4-1)로 이쪽 로컬 타입이 정본.)
 */
export interface DataSourceRowField {
  /** 행 객체 내 경로(표 반복행 셀 바인딩 field 경로와 동일 정본) */
  field: string;
  label?: string;
  /** 편집 위젯 — 'registered'는 host가 widgetKey로 주입하는 등록형 위젯(정본 레지스트리 COMPOSED_ROW_WIDGETS) */
  widget: DataSourceFieldWidget;
  /** widget='registered'일 때 host가 주입하는 등록형 위젯 키(예: 'risk-estimation') */
  widgetKey?: string;
  /**
   * 표시 변형(widget='image'|'annotated-image' 전용, 선언 `DataSourceFieldMap.displayVariant`
   * 통과) — 미지정=전폭(기존 결), 'thumbnail'=카드 폭 30% 작은 사진. 규약 정본은 선언 쪽 JSDoc.
   */
  displayVariant?: 'thumbnail';
  /**
   * widget='signature'일 때 서명자 표시명을 읽어올 **같은 행의 다른 필드명**.
   * 빈 서명 칸에 "홍길동 서명"처럼 누구 자리인지 밝힌다 — 릴레이로 기기를 넘겨 받을 때 필요하다.
   */
  signerNameField?: string;
  /**
   * widget='signature'일 때 파생 대신 고정할 슬롯 키(선언 `DataSourceFieldMap.slotKey` 통과).
   * 소비처는 반드시 `resolveSignatureSlotKey()`로 해소한다(P2 — 파생/고정 규칙 단일 지점).
   */
  slotKey?: string;
  /** 편집 가능 여부(기본 true) */
  editable?: boolean;
  /** 행 그리드 미표시(컬럼·요약 제외) — 행 id/조인 키 등 내부 필드(데이터에는 유지) */
  hidden?: boolean;
  /**
   * **행별** 표시 조건(evaluateCondition 문법 — 그 행 객체로 평가). 거짓이면 그 행 카드에서
   * 이 필드를 렌더하지 않는다. 규약 정본은 선언(`DataSourceFieldMap.visibleWhen`) JSDoc —
   * 같은 field 두 선언을 상보 조건으로 갈라 행 출처별 위젯을 준다.
   */
  visibleWhen?: string;
  /**
   * 수동 추가 행의 초깃값(미지정=위젯별 빈값) + checkbox 미정의 값 표시/판정 폴백.
   * 규약 정본은 선언(`DataSourceFieldMap.defaultValue`) JSDoc.
   */
  defaultValue?: unknown;
}

/** 행 그리드 렌더용 합성 노드 — 편집형 list 선언 1개의 렌더 명세. */
export interface DataSourceRowsNode {
  /** content 키(=def.key, __seed 원본은 이 키+ROWS_SEED_SUFFIX) */
  bindingKey: string;
  label: string;
  /** 행 추가/삭제 상위 스위치(기본 true) — 선언 `DataSourceDef.allowAddRemove` 통과 */
  allowAddRemove?: boolean;
  /** 「+ 행 추가」 축(기본 true) — 선언 `DataSourceDef.allowAdd` 통과. 유효 판정은 `effectiveRowAddRemove` */
  allowAdd?: boolean;
  /** 행별 「×」 축(기본 true) — 선언 `DataSourceDef.allowRemove` 통과 */
  allowRemove?: boolean;
  /** 최대 행 수(선언 `DataSourceDef.rowLimit` 통과) — 도달 시 행 추가 버튼 비활성(삭제는 자유) */
  rowLimit?: number;
  /** 행 필드 명세(선언 fields에서 유도) */
  fields: DataSourceRowField[];
}

/**
 * DataSourceFieldMap(조립/데이터셋 필드 명세) → DataSourceRowField(행 위젯 명세) 어댑터.
 * 두 어휘는 D1 통합으로 동일 — widget 미지정=text(DataSourceRowField.widget은 필수).
 */
export function dataSourceFieldToRowField(field: DataSourceFieldMap): DataSourceRowField {
  return {
    field: field.field,
    label: field.label,
    widget: field.widget ?? 'text',
    widgetKey: field.widgetKey,
    displayVariant: field.displayVariant,
    signerNameField: field.signerNameField,
    slotKey: field.slotKey,
    editable: field.editable,
    hidden: field.hidden,
    visibleWhen: field.visibleWhen,
    defaultValue: field.defaultValue,
  };
}

/** 필드 명세 배열을 행 필드 명세로 매핑. */
export function dataSourceFieldsToRowFields(fields: DataSourceFieldMap[]): DataSourceRowField[] {
  return fields.map(dataSourceFieldToRowField);
}

// 편집형 list 선별(editableListDataSources/isEditableListDataSource)은 dataSourceWidgets.ts가 정본
// — rc 이동(2026-07-20)으로 web 로컬 사본을 제거하고 일원화했다.

/**
 * 편집형 list 선언 하나를 행 그리드 렌더용 합성 노드로 변환.
 * bindingKey=def.key(작성 초안 키, __seed 원본은 이 키+ROWS_SEED_SUFFIX).
 */
export function dataSourceToRowsNode(def: DataSourceDef): DataSourceRowsNode {
  return {
    bindingKey: def.key,
    label: def.label,
    allowAddRemove: def.allowAddRemove,
    allowAdd: def.allowAdd,
    allowRemove: def.allowRemove,
    rowLimit: def.rowLimit,
    fields: dataSourceFieldsToRowFields(def.fields),
  };
}

/**
 * 행 추가가 `rowLimit`에 막혔는가 — 양수 정수 선언 + 현재 행 수가 그 수 이상.
 * (선언 형식 판정은 서버 assembler 컷과 같은 술어 — 비정상 값은 게이트도 무시.)
 * 버튼은 숨기지 않고 **비활성**으로 둔다(왜 못 넣는지 보여야 한다) — title은
 * `rowAddLimitTitle`이 짝으로 만든다.
 */
export function isRowAddBlockedByLimit(
  rowLimit: number | undefined,
  rowCount: number,
): boolean {
  return (
    typeof rowLimit === 'number' &&
    Number.isInteger(rowLimit) &&
    rowLimit > 0 &&
    rowCount >= rowLimit
  );
}

/** rowLimit 도달 시 추가 버튼 title(도달 전/미선언은 undefined — title 자체를 안 단다). */
export function rowAddLimitTitle(
  rowLimit: number | undefined,
  rowCount: number,
): string | undefined {
  return isRowAddBlockedByLimit(rowLimit, rowCount)
    ? `최대 ${rowLimit}행까지만 담을 수 있습니다`
    : undefined;
}

// ============================================================
// 행 그리드 상태 변환(순수 로직 — RowsCollectionEditor가 소비, 구 rowsCollection.ts)
// ============================================================

/** 행 객체 — rowId(정체성) + field별 값. */
export type RowObject = Record<string, unknown> & { rowId?: string };

/** 임의 값을 행 배열로 정규화(배열 아니면 빈 배열). */
export function normalizeRows(value: unknown): RowObject[] {
  return Array.isArray(value) ? (value as RowObject[]) : [];
}

/** textarea-list 필드 값을 문자열 배열로 정규화. */
export function normalizeList(value: unknown): string[] {
  if (Array.isArray(value)) return value.map((v) => (v === undefined || v === null ? '' : String(v)));
  if (value === undefined || value === null || value === '') return [];
  return [String(value)];
}

/**
 * field 위젯별 빈 초기값(수동 추가 행의 필드 seed). 선언 `defaultValue`가 있으면 그 값이 우선
 * (checkbox "기본 켜짐" 등 — 규약은 선언 JSDoc). 그 외 textarea-list/text-list=[],
 * date-range={}(값이 객체인 위젯이라 빈 문자열을 넣으면 컨트롤이 매번 형태를 승격해야 한다),
 * 나머지=''(date 포함 — 값 채널이 yyyy-MM-dd 문자열이라 빈 문자열이 곧 미입력이다).
 *
 * 행 그리드(`DataSourceRowField`)와 formTable 표 위젯 행 추가(`DataDefField` — 위젯 어휘가 더 좁고
 * `defaultValue`가 없다)가 **같은 규약**을 읽는다(2026-08-30 리뷰 INFO — 종전엔 표 위젯 쪽이
 * `date-range ? {} : ''`를 따로 적어 어휘가 늘면 두 곳이 갈렸다). 그래서 입력을 최소 형상으로 받는다.
 */
export function blankFieldValue(field: { widget?: string; defaultValue?: unknown }): unknown {
  if (field.defaultValue !== undefined) return field.defaultValue;
  if (field.widget === 'textarea-list' || field.widget === 'text-list') return [];
  if (field.widget === 'date-range') return {};
  return '';
}

/**
 * 행별 표시 필드 필터 — `visibleWhen` 선언 필드를 그 행 객체로 평가해 거짓이면 뺀다(미선언=표시).
 * 판정은 shared `evaluateCondition`(본문 branch 슬롯과 같은 화이트리스트 문법 — 빈 배열=falsy
 * 특례 포함) 그대로다. 규약 정본은 선언(`DataSourceFieldMap.visibleWhen`) JSDoc.
 */
export function rowVisibleFields(
  fields: DataSourceRowField[],
  row: RowObject,
): DataSourceRowField[] {
  return fields.filter((f) => f.visibleWhen === undefined || evaluateCondition(f.visibleWhen, row));
}

/** 수동 추가 행 1개 생성(rowId=uuid + 각 필드 빈값). */
export function createBlankRow(rowFields: DataSourceRowField[]): RowObject {
  const row: RowObject = { rowId: crypto.randomUUID() };
  for (const f of rowFields) row[f.field] = blankFieldValue(f);
  return row;
}

/** 행 추가(맨 뒤). */
export function addRow(rows: RowObject[], rowFields: DataSourceRowField[]): RowObject[] {
  return [...rows, createBlankRow(rowFields)];
}

/** rowId로 행 삭제. */
export function removeRow(rows: RowObject[], rowId: string | undefined): RowObject[] {
  return rows.filter((r) => r.rowId !== rowId);
}

/** rowId 행의 field 값 갱신(불변). */
export function updateRowField(
  rows: RowObject[],
  rowId: string | undefined,
  field: string,
  value: unknown,
): RowObject[] {
  return rows.map((r) => (r.rowId === rowId ? { ...r, [field]: value } : r));
}

/** seed(원본) 배열에서 같은 rowId 행 조회(없으면 undefined — 수동 추가 행). */
export function findSeedRow(seed: unknown, rowId: string | undefined): RowObject | undefined {
  if (rowId === undefined) return undefined;
  return normalizeRows(seed).find((r) => r.rowId === rowId);
}

/**
 * 비교용 정규화(배열=항목별 문자열 JSON, 객체=JSON, 스칼라=문자열). null/undefined/'' 동일 취급.
 *
 * ⚠️ 객체를 `String()`으로 접으면 값이 무엇이든 '[object Object]'가 되어 **서로 다른 값이 같다고**
 * 판정된다 — 그러면 `isFieldModified`가 늘 false라 그 칸의 ⟲(AI 원본 되돌리기) 버튼이 영영 뜨지
 * 않는다. 값이 객체인 위젯(date-range 등)이 생기기 전부터 잠복해 있던 결함이라 함께 고친다.
 * 빈 객체 `{}`(수동 추가 행의 기간 초깃값)는 '{}'가 되어 빈 문자열 seed와는 다르게 취급되는데,
 * 그 조합은 seed가 없는 수동 추가 행에서만 나오므로(⟲은 seed 행에만) 영향이 없다.
 */
function normalizeForCompare(v: unknown): string {
  if (Array.isArray(v)) return JSON.stringify(v.map((x) => (x === undefined || x === null ? '' : String(x))));
  if (v === undefined || v === null) return '';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

/**
 * 현재 필드 값이 seed 원본과 다른지(되돌리기 버튼 노출 판정).
 * seed 행이 없으면(수동 추가) 항상 false — 되돌릴 원본이 없다.
 */
export function isFieldModified(
  current: unknown,
  seedRow: RowObject | undefined,
  field: string,
): boolean {
  if (!seedRow) return false;
  return normalizeForCompare(current) !== normalizeForCompare(seedRow[field]);
}

// --- 반복 위젯(repeat) 행 어댑터 순수 헬퍼 ---
// 행 식별을 **index**로 한다 — rowId 부재/중복 행이 혼입돼도(외부 조립 데이터 방어) 타 행을
// 오염시키지 않는다(formTable 반복 행 updateCell의 index 규약 동형). rowId 축 헬퍼(updateRowField
// 등)는 행 그리드(RowsCollectionEditor)가 계속 사용.

/**
 * field 경로상의 객체를 복제하며 내려가 원본 행/중간 객체를 미변경으로 남긴다(불변).
 * setByPath는 in-place mutate이므로, 그 전에 경로 세그먼트마다 얕은 복제를 심어
 * 형제 필드는 참조를 보존(React 메모 친화)하고 원본은 건드리지 않게 한다.
 */
function cloneRowAlongPath(row: RowObject, path: string): RowObject {
  const segments = path.split('.');
  const clone: RowObject = { ...row };
  let current: Record<string, unknown> = clone;
  // 마지막 세그먼트는 값 자리 — 그 앞의 중간 객체만 복제(존재하면 spread, 없으면 setByPath가 생성).
  for (let i = 0; i < segments.length - 1; i++) {
    const seg = segments[i];
    if (seg === '__proto__' || seg === 'constructor' || seg === 'prototype') break;
    const next = current[seg];
    if (next !== null && typeof next === 'object') {
      current[seg] = { ...(next as Record<string, unknown>) };
      current = current[seg] as Record<string, unknown>;
    } else {
      break; // 중간 객체 부재 — setByPath가 이후 세그먼트를 생성한다.
    }
  }
  return clone;
}

/**
 * idx 행의 field 값 갱신(불변) — 반복 위젯 rowOnChange 어댑터(해당 행만 갱신·타 행 보존).
 * field에 점이 있으면(`riskEstimation.likelihood`) 중첩 경로에 기록(형제 필드 보존),
 * 없으면 현행 flat set. read 경로(readBindingValue: flat 우선 + getByPath 폴백)와 왕복 정합.
 */
export function updateRowAtIndex(
  rows: RowObject[],
  idx: number,
  field: string,
  value: unknown,
): RowObject[] {
  return rows.map((r, i) => {
    if (i !== idx) return r;
    if (!field.includes('.')) return { ...r, [field]: value };
    const clone = cloneRowAlongPath(r, field);
    setByPath(clone, field, value);
    return clone;
  });
}

/**
 * 행 스코프 쓰기 pending — 반복 행(RepeatRowsBlock)·행 그리드(RowsCollectionEditor) 두 경로가 공유하는
 * 「함수형 갱신의 prev」. 한 이벤트에서 onChange가 **동기로 여러 번** 불리는 위젯(콤보 선택 = 자기
 * 텍스트 + 합성 id + writes 전파 일괄)은, 각 호출이 렌더 시점 rows에서 출발하면 host setValues가 키
 * 단위 병합이라 같은 키를 연속 덮어써 **마지막 호출만 살아남는다**(2026-08-03 SMS 개선계획 담당 콤보
 * 실사고 — 보이는 텍스트가 첫 호출이라 "선택해도 반영 안 됨"으로 발현). pending 누적으로 직전 호출
 * 결과 위에 갱신을 쌓는다. 본문 스코프 콤보는 호출마다 다른 키에 써서 이 문제가 없다(행 스코프 전용).
 *
 * pending의 수명(2026-09-14 개정 — 비동기 커밋 병렬 유실 차단): 종전엔 호출측이 **매 렌더 후** pending을
 * 비워 다음 이벤트가 fresh rows에서 출발했다. 그런데 행 안 위젯이 **비동기로 시차를 두고** 커밋하면
 * (improvement-image 진입 시 자동 생성 — 행 N개 병렬) 「A 커밋 → 렌더 → B 커밋(pending) → 패시브
 * effect가 pending 초기화 → C 커밋이 직전 렌더 스냅샷(A만)에서 출발」로 B가 유실된다. 호스트
 * setValues는 함수형이어도 우리가 넘기는 값이 행 배열 통째라 prev를 못 쓴다. 그래서 pending을
 * **함수형 갱신의 prev**로 삼는다: `sent`에 우리가 올려보낸 배열을 기록하고, 호스트 렌더로 되돌아온
 * rows가 그중 하나면(우리 커밋의 반영 — 지연됐을 뿐) pending을 유지, 우리가 만든 적 없는 배열이면
 * (prefill·되돌리기·followKey 등 외부 변경) pending을 버린다(`reconcileRowScopedPending`). 결과: 커밋은
 * 늘 「마지막으로 알려진 rows」에서 출발한다.
 *
 * 행 그리드도 같은 pending을 쓴다(`commitRowScopedRows`).
 */
export interface RowScopedPending {
  /** 마지막으로 올려보낸 행 배열(=다음 커밋의 prev). null이면 렌더 시점 rows에서 출발. */
  current: Record<string, unknown>[] | null;
  /** 우리가 올려보낸 배열들(참조) — 되돌아온 rows가 우리 것인지 판별. 미지정이면 매 렌더 초기화 규약. */
  sent?: WeakSet<object>;
}

/**
 * 행 스코프 쓰기 어댑터 팩토리 — 반복 행 안 위젯의 onChange(field, v)를 onChange(over, 행 배열)로
 * 변환한다(규약은 `RowScopedPending` JSDoc).
 */
export function makeRowScopedOnChange(
  over: string,
  rows: Record<string, unknown>[],
  idx: number,
  pending: RowScopedPending,
  onChange: (bindingKey: string, value: unknown) => void,
): (field: string, v: unknown) => void {
  return (field, v) => {
    const next = updateRowAtIndex(pending.current ?? rows, idx, field, v);
    commitRowScopedRows(pending, next);
    onChange(over, next);
  };
}

/**
 * pending에 「올려보낸 배열」을 기록한다 — 행 그리드처럼 갱신 함수가 여럿(필드 갱신·행 추가/삭제·
 * 되돌리기)인 경로가 `updateRowField(pending.current ?? rows, …)`로 만든 결과를 호스트에 넘기기 직전에
 * 부른다. makeRowScopedOnChange는 이 함수를 안에서 부른다.
 */
export function commitRowScopedRows(
  pending: RowScopedPending,
  next: Record<string, unknown>[],
): void {
  pending.current = next;
  pending.sent?.add(next);
}

/**
 * 렌더 후 pending 정리 — 호스트에서 내려온 `incomingRows`가 우리가 올려보낸 배열(sent)이면 pending을
 * 유지(지연 반영 중일 뿐, pending이 더 새롭거나 같다), 아니면 외부 변경이라 pending을 버려 호스트를
 * 정본으로 삼는다. `sent`가 없으면 종전 규약(매 렌더 초기화).
 *
 * 전제: 호스트가 우리 배열을 **그대로**(identity) 돌려준다. 실호스트 셋(web new-composed·external
 * composed·admin SimulationPage)의 handleChange는 전부 `setValues(prev => ({...prev, [k]: v}))`라 성립
 * (2026-09-14 grep). 호스트가 복사·정규화해 돌려주면 매번 외부 변경으로 보여 pending이 폐기되지만,
 * 그때도 종전(최신 onChange + 같은 배치 pending)보다 나빠지지 않는다 — web
 * ImprovementImageWidgetAuto spec의 복사 호스트 대조군이 그 하한을 고정한다.
 */
export function reconcileRowScopedPending(pending: RowScopedPending, incomingRows: unknown): void {
  if (pending.current === null) return;
  if (pending.sent && typeof incomingRows === 'object' && incomingRows !== null && pending.sent.has(incomingRows)) {
    return;
  }
  pending.current = null;
}

/** idx 행 삭제(불변). */
export function removeRowAtIndex(rows: RowObject[], idx: number): RowObject[] {
  return rows.filter((_, i) => i !== idx);
}

/** idx 행을 row로 통째 교체(불변) — seed 되돌리기(행 단위 원본 복원)용. */
export function replaceRowAtIndex(rows: RowObject[], idx: number, row: RowObject): RowObject[] {
  return rows.map((r, i) => (i === idx ? { ...row } : r));
}

/**
 * 행이 seed 원본과 다른지(행 단위 되돌리기 버튼 노출 판정) — 선언 필드(rowFields)만 대조한다
 * (콤보 합성 id 등 `__` 접두 합성 키·비선언 키는 판정 제외 — 복원 시엔 행 통째 교체라 함께 복원됨).
 * seed 행이 없으면(수동 추가 행) 항상 false.
 */
export function isRowModified(
  row: RowObject,
  seedRow: RowObject | undefined,
  rowFields: DataSourceRowField[],
): boolean {
  if (!seedRow) return false;
  return rowFields.some((f) => isFieldModified(row[f.field], seedRow, f.field));
}

// --- textarea-list(문자열 배열) 항목 조작 ---

/** 항목 추가(맨 뒤 빈 문자열). */
export function addListItem(list: string[]): string[] {
  return [...list, ''];
}

/** idx 항목 삭제. */
export function removeListItem(list: string[], idx: number): string[] {
  return list.filter((_, i) => i !== idx);
}

/** idx 항목 값 갱신. */
export function updateListItem(list: string[], idx: number, value: string): string[] {
  return list.map((item, i) => (i === idx ? value : item));
}

/**
 * 클릭 진입(editEntry:'click') 편집 세션 종료 시의 목록 — **내용 없이 끝낸 항목은 남기지 않는다.**
 * "+ 항목 추가" 후 아무것도 입력하지 않고 취소(blur/Esc)하면 빈 항목이 `클릭하여 입력` 자리로
 * 남아 있었다(2026-08-05 사용자 제보): 추가를 취소한 것이므로 그 항목은 없어야 한다. 기존 항목을
 * 지우고 나간 경우도 같다 — 빈 항목은 목록에서 의미가 없다(비우기=삭제).
 * 변경이 필요 없으면 **null**을 반환한다(불필요한 onChange 방지).
 *
 * ⚠️ always 모드(상시 입력)에는 적용하지 않는다 — 거기엔 "편집 세션"이 없어서, 탭으로 지나가는
 * 빈 칸이 사라져 버린다.
 */
export function listAfterEditEnd(list: string[], idx: number): string[] | null {
  if (idx < 0 || idx >= list.length) return null;
  if (list[idx].trim() !== '') return null;
  return removeListItem(list, idx);
}

/**
 * "+ 항목 추가" 직전의 기준 목록 — 편집 중이던 항목이 비어 있으면 그것을 먼저 걷어낸다.
 * (빈 항목을 열어 둔 채 또 추가하면 빈 줄이 쌓인다 — 추가는 언제나 "빈 항목 1개"만 만든다.)
 */
export function listBeforeAddItem(list: string[], editingIdx: number | null): string[] {
  if (editingIdx === null) return list;
  return listAfterEditEnd(list, editingIdx) ?? list;
}

/**
 * 선택 전용 칸(SuggestionsFromConfig.requireSelection)이 있는 **표**에서 편집 세션이 끝났을 때의
 * 행 목록 — `listAfterEditEnd`의 표 판이다(같은 규약: 내용 없이 끝낸 *추가*는 남기지 않는다).
 *
 * 선택 전용 칸은 타이핑이 값이 되지 않으므로, 검색만 하고 아무것도 고르지 않은 채 빠져나오면
 * 그 행은 빈 껍데기로 남는다. 방금 추가한 행(`__custom` 마킹)이 그러면 추가를 취소한 것으로 본다.
 *
 * ⚠️ **추가한 행만** 대상이다(`__custom`) — 이미 있던 행을 편집하다 취소하면 원래 값이 유지되어야
 * 하고, 서버가 실어 준 행은 애초에 사용자가 만든 것이 아니다.
 * ⚠️ requiredKeys가 **전부** 비었을 때만 지운다 — 여러 칸 중 하나라도 골라 뒀으면 사용자의 작업물이다.
 * 변경이 필요 없으면 **null**을 반환한다(불필요한 onChange 방지 — listAfterEditEnd 규약 동형).
 */
export function rowsAfterRequiredSelectionEditEnd(
  rows: Record<string, unknown>[],
  idx: number,
  requiredKeys: string[],
): Record<string, unknown>[] | null {
  if (requiredKeys.length === 0) return null;
  if (idx < 0 || idx >= rows.length) return null;
  if (!isCancelledAddRow(rows[idx], requiredKeys)) return null;
  return rows.filter((_, i) => i !== idx);
}

/** 아무것도 고르지 않은 채 남은 **추가 행**인가(위 두 ⚠️ 규약의 판정부 — 두 헬퍼가 공유). */
function isCancelledAddRow(
  row: Record<string, unknown> | undefined,
  requiredKeys: string[],
): boolean {
  if (!row || row.__custom !== true) return false;
  return requiredKeys.every((k) => {
    const v = row[k];
    return v === undefined || v === null || String(v).trim() === '';
  });
}

/**
 * 추가 행(`__custom`)의 **입력 확정** 판정 — 그 행의 읽기전용 칼럼(`readonlyFields`)이 전부
 * 채워졌으면 마킹을 지운 행 목록을, 아직이면 **null**(변경 없음)을 돌려준다.
 *
 * 마킹이 지워진 행은 기본 제공 행과 같이 잠긴다(구 SMS 동형: 추가할 때만 입력, 정정은 삭제 후
 * 재추가). 확정은 두 시점에서 일어난다 — 칸을 빠져나올 때(blur), 그리고 선택 전용 칸에서
 * **제안을 고른 순간**(고른 값은 이미 확정이라 blur를 기다릴 이유가 없다).
 *
 * ⚠️ 판정 대상 목록을 인자로 받는 것이 이 헬퍼의 요점이다 — 선택 경로는 **방금 쓴 값이 반영된**
 * 목록으로 판정해야 한다(값 쓰기 전 목록으로 판정하면 "미입력 행"이라 잠금이 걸리지 않는다).
 * readonlyKeys가 빈 표는 채울 것이 없으므로 곧바로 확정이다(종전 동작).
 */
export function rowsAfterAddedRowConfirm(
  rows: Record<string, unknown>[],
  idx: number,
  readonlyKeys: string[],
): Record<string, unknown>[] | null {
  const row = rows[idx];
  if (!row || row.__custom !== true) return null;
  const filled = readonlyKeys.every((k) => {
    const v = row[k];
    return v !== undefined && v !== null && v !== '';
  });
  if (!filled) return null;
  return rows.map((r, i) => {
    if (i !== idx) return r;
    // omit-구조분해는 rc lint가 미사용 변수로 거부 — 복사 후 delete로 마킹 제거.
    const rest = { ...r };
    delete rest.__custom;
    return rest;
  });
}

/**
 * "+ 항목 추가" 직전의 기준 행 목록 — 목록형 `listBeforeAddItem`의 **표 판**이다(같은 규약:
 * 추가는 언제나 "빈 행 1개"만 만든다).
 *
 * 표는 목록형과 달리 편집 중 인덱스를 들고 있지 않다. 추가 버튼으로 넘어간 blur는 `endCellEdit`이
 * 그 버튼에 조작을 양보하려고 조기 return하므로(그러지 않으면 인덱스가 밀려 엉뚱한 행이 지워진다),
 * 아무것도 고르지 않은 채 추가를 반복하면 빈 행이 그대로 쌓여 저장값에 남았다. 그래서 인덱스가
 * 아니라 **행 전체**에서 취소된 추가 행을 걷어낸다.
 * 변경이 없으면 원 배열을 그대로 돌려준다(불필요한 onChange 방지 — 위 헬퍼들과 같은 규약).
 */
export function rowsBeforeAddRow(
  rows: Record<string, unknown>[],
  requiredKeys: string[],
): Record<string, unknown>[] {
  if (requiredKeys.length === 0) return rows;
  const kept = rows.filter((row) => !isCancelledAddRow(row, requiredKeys));
  return kept.length === rows.length ? rows : kept;
}

// ============================================================
// 행 접기/펴기(collapse) UI 상태 순수 헬퍼
// (RowsCollectionEditor가 소비 — 구 SMS 위험성평가 결과 행 접기 UX 동형)
// ============================================================

/**
 * 요약 헤더에 표시할 텍스트 위젯(문자열 스칼라 값) 판별.
 *
 * 'date'를 포함한다 — 접힌 행을 식별하는 데 날짜는 이름만큼 쓸모가 있고(점검일·발생일),
 * 값이 문자열 스칼라라 이 집합의 전제("텍스트로 접을 수 있다")를 그대로 만족한다. 표기는
 * `summaryText`가 한글 라벨로 접는다(그리드 안 다른 표시와 같은 글자).
 *
 * 'date-range'는 **제외**한다 — 값이 객체라 이 집합의 전제를 깨고, 요약 한 줄에 "A ~ B"가
 * 들어가면 잘림(summaryPrimaryStyle의 ellipsis)만 남기 쉽다. 'number'를 뺀 것과 같은 결의 판단이다.
 */
const SUMMARY_TEXT_WIDGETS: ReadonlySet<DataSourceFieldWidget> = new Set([
  'text',
  'textarea',
  'date',
]);

/** 요약 헤더에 표시할 텍스트 필드 — 선언 순서상 처음 두 개. */
export function summaryFieldsForRow(rowFields: DataSourceRowField[]): DataSourceRowField[] {
  // hidden(내부 식별 필드) 제외 — 구 SMS 요약(구분·위험요인)처럼 사용자 정보만 노출.
  return rowFields.filter((f) => !f.hidden && SUMMARY_TEXT_WIDGETS.has(f.widget)).slice(0, 2);
}

/** 행의 안정적 접힘 키(rowId 축 — index와 달리 삭제/재정렬에 불변, rowId 없으면 index 폴백). */
export function rowCollapseKey(row: RowObject, rowIdx: number): string {
  return row.rowId ?? `__idx_${rowIdx}`;
}

/**
 * 요약 헤더 텍스트: 값이 있으면 그 값, 비었으면 `(라벨 미입력)`.
 * 날짜 계열은 한글 라벨로 접는다(`dateWidgetDisplayText`) — 같은 값이 카드 안에서는
 * "2026년 3월 5일"인데 접힌 헤더에서는 "2026-03-05"로 보이면 다른 값처럼 읽힌다.
 */
export function summaryText(row: RowObject, field: DataSourceRowField): string {
  const raw = row[field.field];
  const dateText = dateWidgetDisplayText(field.widget, raw);
  const str = dateText !== undefined ? dateText : raw === undefined || raw === null ? '' : String(raw);
  return str.trim() !== '' ? str : `(${field.label ?? field.field} 미입력)`;
}

/** 모든 행이 접혔는지(모두 접기/펴기 토글 판정). 행이 없으면 false. */
export function isAllRowsCollapsed(rows: RowObject[], collapsedKeys: ReadonlySet<string>): boolean {
  return rows.length > 0 && rows.every((row, i) => collapsedKeys.has(rowCollapseKey(row, i)));
}

/** 단일 key 접힘 토글(불변 Set 반환). */
export function toggleCollapseKey(prev: ReadonlySet<string>, key: string): Set<string> {
  const next = new Set(prev);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
}

/** 모두 접기/펴기 결과 Set(전부 접힘이면 모두 펴기=빈 Set, 아니면 전 행 key). */
export function collapsedForToggleAll(rows: RowObject[], allCollapsed: boolean): Set<string> {
  return allCollapsed ? new Set() : new Set(rows.map((row, i) => rowCollapseKey(row, i)));
}

/** 행 삭제 후 접힘 Set 정리(삭제 행 key 제거 — rowId 축이라 나머지는 불변). */
export function collapsedAfterRemove(prev: ReadonlySet<string>, key: string): Set<string> {
  if (!prev.has(key)) return new Set(prev);
  const next = new Set(prev);
  next.delete(key);
  return next;
}
